import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  AddDemoProductsResponse,
  demosApi,
  DemoListItem,
  DemoPurgeManifest,
  ExtendDemoResponse,
  ExtendTrialResponse,
  InviteDemoBrandUserResponse,
  OutreachCertificateView,
  ProposeDemoResponse,
  ProposedProductSeedResult,
  ProvisionDemoBody,
  ProvisionDemoProductInput,
  ProvisionDemoResponse,
  TakeOverDemoResponse,
} from 'services/demosApi'

const DEMOS_QUERY_KEY = 'internal-demos'
const OUTREACH_QUERY_KEY = 'demo-outreach-certificate'

/** VRG-723 — a demo's outreach certificate (null id = nothing to load). */
export const useOutreachCertificate = (companyId: string | null | undefined) =>
  useQuery<OutreachCertificateView>({
    queryKey: [OUTREACH_QUERY_KEY, companyId],
    queryFn: () => demosApi.getOutreachCertificate(companyId as string),
    enabled: !!companyId,
    staleTime: 15_000,
  })

/** VRG-723 — change it. Refreshes the panel and the Demos list (whose row names the product). */
export const useSetOutreachCertificate = () => {
  const queryClient = useQueryClient()
  return useMutation<OutreachCertificateView, Error, { companyId: string; productId: string }>({
    mutationFn: ({ companyId, productId }) => demosApi.setOutreachCertificate(companyId, productId),
    onSuccess: (view) => {
      queryClient.setQueryData([OUTREACH_QUERY_KEY, view.companyId], view)
      queryClient.invalidateQueries({ queryKey: [DEMOS_QUERY_KEY] })
    },
  })
}

/** VRG-311 AC4 — list of acquisition demos with engagement/age state. */
export const useDemos = (includePurged: boolean) =>
  useQuery<DemoListItem[]>({
    queryKey: [DEMOS_QUERY_KEY, includePurged],
    queryFn: () => demosApi.listDemos(includePurged),
    staleTime: 15_000,
  })

/** Proposals only — nothing is written (VRG-311 AC3). */
export const useProposeDemo = () =>
  useMutation<ProposeDemoResponse, unknown, string>({
    mutationFn: (url) => demosApi.proposeDemo(url),
  })

/** VRG-716 — one product from a pasted listing URL. A proposal only; nothing is written. */
export const useImportDemoProduct = () =>
  useMutation<ProposedProductSeedResult, unknown, string>({
    mutationFn: (url) => demosApi.importDemoProduct(url),
  })

export const useProvisionDemo = () => {
  const queryClient = useQueryClient()
  return useMutation<ProvisionDemoResponse, unknown, ProvisionDemoBody>({
    mutationFn: (body) => demosApi.provisionDemo(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DEMOS_QUERY_KEY] })
      // VRG-723 — the demo is linked to (or created) a GTM → Sales company: refresh Sales too.
      queryClient.invalidateQueries({ queryKey: ['internal', 'gtm', 'sales'] })
    },
  })
}

/**
 * VRG-369 — add products to an existing demo from the console, no space entry.
 *
 * Invalidates the demos list because the row's `updated_at` moves and, more to the
 * point, so an AE who adds products and then purges is not acting on a stale row.
 */
export const useAddDemoProducts = () => {
  const queryClient = useQueryClient()
  return useMutation<
    AddDemoProductsResponse,
    unknown,
    { companyId: string; products: ProvisionDemoProductInput[] }
  >({
    mutationFn: ({ companyId, products }) => demosApi.addDemoProducts(companyId, products),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DEMOS_QUERY_KEY] })
    },
  })
}

/** VRG-361 — change a demo's lifetime. `expiresAt: null` means never expires. */
export const useExtendDemo = () => {
  const queryClient = useQueryClient()
  return useMutation<
    ExtendDemoResponse,
    unknown,
    { companyId: string; expiresAt: string | null }
  >({
    mutationFn: ({ companyId, expiresAt }) => demosApi.extendDemo(companyId, expiresAt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DEMOS_QUERY_KEY] })
    },
  })
}

/**
 * VRG-361 — change a demo's console trial deadline. `trialEndsAt` is always an
 * ISO date (the API requires it, unlike `expiresAt` on `useExtendDemo` — an
 * unbounded trial has no defined meaning here).
 */
export const useExtendTrial = () => {
  const queryClient = useQueryClient()
  return useMutation<ExtendTrialResponse, unknown, { companyId: string; trialEndsAt: string }>({
    mutationFn: ({ companyId, trialEndsAt }) => demosApi.extendTrial(companyId, trialEndsAt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DEMOS_QUERY_KEY] })
    },
  })
}

/**
 * VRG-361 Slice 2 — invite a brand admin into a demo.
 *
 * Invalidates the demos list because `invitedUserCount` moves on the row, which is
 * what tells the AE the invitation is out. It does NOT invalidate `['companies']`:
 * unlike the takeover below, nothing about what the company IS has changed — a
 * membership row was added to it.
 */
export const useInviteDemoBrandUser = () => {
  const queryClient = useQueryClient()
  return useMutation<
    InviteDemoBrandUserResponse,
    unknown,
    { companyId: string; emails: string[] }
  >({
    mutationFn: ({ companyId, emails }) => demosApi.inviteDemoBrandUser(companyId, emails),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DEMOS_QUERY_KEY] })
    },
  })
}

/**
 * VRG-361 Slice 2 — convert a demo into a real, billable customer account.
 *
 * Invalidates the demos list AND every company-shaped query, because this is the
 * one demo action that changes what the company IS: `kind` leaves `demo` and
 * `is_example` goes false, which the internal Companies list, the company detail
 * page and the Vera-space grant resolver all read. Invalidating only the demos
 * key would leave the rest of the console showing a demo that no longer exists.
 */
export const useTakeOverDemo = () => {
  const queryClient = useQueryClient()
  return useMutation<TakeOverDemoResponse, unknown, { companyId: string }>({
    mutationFn: ({ companyId }) => demosApi.takeOverDemo(companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DEMOS_QUERY_KEY] })
      // Prefix invalidation: `['companies', 'list', …]` and
      // `['companies', 'detail', id]` (useCompaniesQuery / useCompanyQuery) both
      // hang off this key, so one call covers the list and every detail page.
      queryClient.invalidateQueries({ queryKey: ['companies'] })
    },
  })
}

export const usePurgeDemo = () => {
  const queryClient = useQueryClient()
  return useMutation<DemoPurgeManifest, unknown, { companyId: string; reason: string }>({
    mutationFn: ({ companyId, reason }) => demosApi.purgeDemo(companyId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DEMOS_QUERY_KEY] })
    },
  })
}
